import "katex/dist/katex.min.css";
import "prismjs/themes/prism-tomorrow.css";
import "./style.css";

import type { ViewMode } from "./types";
import { initToast, toast } from "./utils";
import { renderMarkdown } from "./renderer";
import { buildToc } from "./toc";
import { getPageContent } from "./page-content";
import { createShare } from "./share-api";
import { showModal, setUrl, setError } from "./share-modal";

// --- DOM refs ---

const $ = (s: string) => document.querySelector(s);

const editor = $("#editor") as HTMLTextAreaElement;
const preview = $("#preview") as HTMLElement;
const previewScroll = $("#preview-scroll") as HTMLElement;
const main = $("#main") as HTMLElement;
const tocDropdown = $("#toc-dropdown") as HTMLElement;
const tocList = $("#toc-list") as HTMLElement;
const fileInput = $("#file-input") as HTMLInputElement;
const fileNameEl = $("#file-name") as HTMLElement;
const toastEl = $("#toast") as HTMLElement;
const editorPanel = $("#editor-panel") as HTMLElement;
const resizeHandle = $("#resize-handle") as HTMLElement;

const btnOpen = $("#btn-open") as HTMLButtonElement;
const btnTheme = $("#btn-theme") as HTMLButtonElement;
const btnMore = $("#btn-more") as HTMLButtonElement | null;
const moreMenu = $("#more-menu") as HTMLElement | null;
const btnToc = $("#btn-toc") as HTMLButtonElement;
const btnCopy = $("#btn-copy") as HTMLButtonElement;
const btnPrint = $("#btn-print") as HTMLButtonElement;
const btnShare = $("#btn-share") as HTMLButtonElement;
const btnViewEditor = $("#btn-view-editor") as HTMLButtonElement;
const btnViewSplit = $("#btn-view-split") as HTMLButtonElement;
const btnViewPreview = $("#btn-view-preview") as HTMLButtonElement;

const iconMoon = $("#icon-moon") as HTMLElement;
const iconSun = $("#icon-sun") as HTMLElement;

// --- State ---

let currentView: ViewMode = "split";
let tocVisible = false;
let renderTimer: ReturnType<typeof setTimeout> | undefined;
let syncing = false;

// --- Toast init ---

initToast(toastEl);

// --- Render pipeline ---

function render(): void {
  renderMarkdown(editor.value, preview);
  buildToc(preview, tocList, previewScroll);
  updateStats(editor.value);
}

function scheduleRender(): void {
  clearTimeout(renderTimer);
  renderTimer = setTimeout(render, 120);
}

// --- Stats ---

function updateStats(md: string): void {
  const text = md.trim();
  const words = text ? text.split(/\s+/).length : 0;
  const chars = text.length;
  ($("#stat-words") as HTMLElement).textContent =
    `${words} word${words !== 1 ? "s" : ""}`;
  ($("#stat-chars") as HTMLElement).textContent =
    `${chars} char${chars !== 1 ? "s" : ""}`;
}

// --- Theme ---

function setTheme(theme: string): void {
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem("md-viewer-theme", theme);
  if (theme === "dark") {
    iconMoon.style.display = "";
    iconSun.style.display = "none";
  } else {
    iconMoon.style.display = "none";
    iconSun.style.display = "";
  }
}

function toggleTheme(): void {
  const curr = document.documentElement.getAttribute("data-theme");
  setTheme(curr === "dark" ? "light" : "dark");
}

// --- Mobile detection ---

const MOBILE_BREAKPOINT = 600;

function isMobile(): boolean {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

// --- View Mode ---

function setView(mode: ViewMode): void {
  currentView = mode;
  main.className = `main view-${mode}`;
  [btnViewEditor, btnViewSplit, btnViewPreview].forEach((b) =>
    b.classList.remove("active"),
  );
  if (mode === "editor") btnViewEditor.classList.add("active");
  if (mode === "split") btnViewSplit.classList.add("active");
  if (mode === "preview") btnViewPreview.classList.add("active");
}

// --- TOC Toggle ---

function toggleToc(): void {
  tocVisible = !tocVisible;
  tocDropdown.classList.toggle("hidden", !tocVisible);
  btnToc.classList.toggle("active", tocVisible);
}

function closeToc(): void {
  if (!tocVisible) return;
  tocVisible = false;
  tocDropdown.classList.add("hidden");
  btnToc.classList.remove("active");
}

// Close dropdown when clicking outside
document.addEventListener("mousedown", (e) => {
  if (
    tocVisible &&
    !tocDropdown.contains(e.target as Node) &&
    !btnToc.contains(e.target as Node)
  ) {
    closeToc();
  }
});

// Close dropdown when clicking a TOC link
tocDropdown.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest(".toc-item")) {
    closeToc();
  }
});

// --- More Menu ---

let moreVisible = false;

function toggleMore(): void {
  if (!btnMore || !moreMenu) return;
  moreVisible = !moreVisible;
  moreMenu.classList.toggle("hidden", !moreVisible);
  btnMore.classList.toggle("active", moreVisible);
}

function closeMore(): void {
  if (!moreVisible || !btnMore || !moreMenu) return;
  moreVisible = false;
  moreMenu.classList.add("hidden");
  btnMore.classList.remove("active");
}

document.addEventListener("mousedown", (e) => {
  if (
    moreVisible &&
    moreMenu &&
    btnMore &&
    !moreMenu.contains(e.target as Node) &&
    !btnMore.contains(e.target as Node)
  ) {
    closeMore();
  }
});

moreMenu?.addEventListener("click", () => {
  closeMore();
});

// --- File Handling ---

function loadFile(file: File): void {
  const reader = new FileReader();
  reader.onload = (e) => {
    editor.value = (e.target as FileReader).result as string;
    fileNameEl.textContent = file.name;
    render();
    toast(`Loaded ${file.name}`);
  };
  reader.readAsText(file);
}

// --- Drag & Drop ---

let dragCounter = 0;

editorPanel.addEventListener("dragenter", (e) => {
  e.preventDefault();
  dragCounter++;
  editorPanel.classList.add("dragover");
});

editorPanel.addEventListener("dragleave", (e) => {
  e.preventDefault();
  dragCounter--;
  if (dragCounter <= 0) {
    dragCounter = 0;
    editorPanel.classList.remove("dragover");
  }
});

editorPanel.addEventListener("dragover", (e) => e.preventDefault());

editorPanel.addEventListener("drop", (e) => {
  e.preventDefault();
  dragCounter = 0;
  editorPanel.classList.remove("dragover");
  const file = e.dataTransfer?.files[0];
  if (file) loadFile(file);
});

document.addEventListener("dragover", (e) => e.preventDefault());
document.addEventListener("drop", (e) => {
  e.preventDefault();
  const file = e.dataTransfer?.files?.[0];
  if (file) loadFile(file);
});

// --- Resize Handle ---

let isResizing = false;

resizeHandle.addEventListener("mousedown", (e) => {
  isResizing = true;
  resizeHandle.classList.add("active");
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  e.preventDefault();
});

document.addEventListener("mousemove", (e) => {
  if (!isResizing) return;
  const rect = main.getBoundingClientRect();
  const offset = e.clientX - rect.left;
  const pct = (offset / rect.width) * 100;
  if (pct > 15 && pct < 85) {
    editorPanel.style.flex = `0 0 ${pct}%`;
    ($("#preview-panel") as HTMLElement).style.flex = "1";
  }
});

document.addEventListener("mouseup", () => {
  if (isResizing) {
    isResizing = false;
    resizeHandle.classList.remove("active");
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }
});

// Touch support for resize handle
resizeHandle.addEventListener("touchstart", (e) => {
  isResizing = true;
  resizeHandle.classList.add("active");
  e.preventDefault();
});

document.addEventListener("touchmove", (e) => {
  if (!isResizing) return;
  const touch = e.touches[0];
  const rect = main.getBoundingClientRect();
  const offset = touch.clientX - rect.left;
  const pct = (offset / rect.width) * 100;
  if (pct > 15 && pct < 85) {
    editorPanel.style.flex = `0 0 ${pct}%`;
    ($("#preview-panel") as HTMLElement).style.flex = "1";
  }
});

document.addEventListener("touchend", () => {
  if (isResizing) {
    isResizing = false;
    resizeHandle.classList.remove("active");
  }
});

// --- Scroll Sync ---

function syncScroll(source: HTMLElement, target: HTMLElement): void {
  if (syncing || currentView !== "split") return;
  syncing = true;
  const maxSource = source.scrollHeight - source.clientHeight;
  const maxTarget = target.scrollHeight - target.clientHeight;
  if (maxSource > 0 && maxTarget > 0) {
    const pct = source.scrollTop / maxSource;
    target.style.scrollBehavior = "auto";
    target.scrollTop = pct * maxTarget;
    target.style.scrollBehavior = "";
  }
  requestAnimationFrame(() => {
    syncing = false;
  });
}

editor.addEventListener("scroll", () => syncScroll(editor, previewScroll));
previewScroll.addEventListener("scroll", () =>
  syncScroll(previewScroll, editor),
);

// --- Event Listeners ---

editor.addEventListener("input", scheduleRender);
btnOpen.addEventListener("click", () => fileInput.click());
fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file) loadFile(file);
});
btnTheme.addEventListener("click", toggleTheme);
btnMore?.addEventListener("click", toggleMore);
btnToc.addEventListener("click", toggleToc);
btnViewEditor.addEventListener("click", () => setView("editor"));
btnViewSplit.addEventListener("click", () => setView("split"));
btnViewPreview.addEventListener("click", () => setView("preview"));

btnCopy.addEventListener("click", () => {
  navigator.clipboard
    .writeText(preview.innerHTML)
    .then(() => toast("HTML copied to clipboard"));
});

btnPrint.addEventListener("click", () => window.print());

btnShare.addEventListener("click", async () => {
  const content = editor.value.trim();
  if (!content) {
    toast("Nothing to share — write some Markdown first.");
    return;
  }
  showModal();
  try {
    const result = await createShare(content);
    setUrl(result.url);
  } catch (err) {
    setError(
      err instanceof Error ? err.message : "Failed to create share link.",
    );
  }
});

// --- Mobile more-menu button wiring ---

$("#btn-toc-m")?.addEventListener("click", toggleToc);
$("#btn-copy-m")?.addEventListener("click", () => {
  navigator.clipboard
    .writeText(preview.innerHTML)
    .then(() => toast("HTML copied to clipboard"));
});
$("#btn-print-m")?.addEventListener("click", () => window.print());
$("#btn-share-m")?.addEventListener("click", () => btnShare.click());

// --- Keyboard shortcuts ---

document.addEventListener("keydown", (e) => {
  const mod = e.metaKey || e.ctrlKey;
  if (mod && e.key === "o") {
    e.preventDefault();
    fileInput.click();
  }
  if (mod && e.key === "b") {
    e.preventDefault();
    toggleToc();
  }
  if (mod && e.key === "p") {
    e.preventDefault();
    window.print();
  }
  if (mod && e.shiftKey && e.key === "C") {
    e.preventDefault();
    navigator.clipboard
      .writeText(preview.innerHTML)
      .then(() => toast("HTML copied"));
  }
  if (mod && e.key === "1") {
    e.preventDefault();
    setView("editor");
  }
  if (mod && e.key === "2") {
    e.preventDefault();
    setView("split");
  }
  if (mod && e.key === "3") {
    e.preventDefault();
    setView("preview");
  }
});

// Tab in editor
editor.addEventListener("keydown", (e) => {
  if (e.key === "Tab") {
    e.preventDefault();
    const start = editor.selectionStart;
    const end = editor.selectionEnd;
    editor.value =
      editor.value.substring(0, start) + "  " + editor.value.substring(end);
    editor.selectionStart = editor.selectionEnd = start + 2;
    scheduleRender();
  }
});

// --- Init ---

void (function init() {
  const savedTheme = localStorage.getItem("md-viewer-theme") || "light";
  setTheme(savedTheme);
  editor.value = getPageContent();
  render();

  // Auto-switch to preview on mobile
  if (isMobile()) {
    setView("preview");
  }
})();

// Switch away from split view when resizing to mobile
window.addEventListener("resize", () => {
  if (isMobile() && currentView === "split") {
    setView("preview");
  }
});
